/**
 * Edit Profile Identity: business-error model + mapper.
 *
 * Same shape as the other form slices: a coarse infra failure (`network` | `api`,
 * from the app-wide `@/utils/infra-error`) OR a business `validation` failure
 * carrying per-field messages.
 *
 * The one flow-specific rule is the 409: the `linkedin_url` column is UNIQUE, so
 * "another profile already uses this URL" is an expected outcome about ONE field
 * and belongs under that field, not in a snackbar.
 */
import { classifyInfraError, type InfraFailure } from '@/utils/infra-error';

import type { ValidationErrors } from './profile-identity-draft.validation';
import { LINKEDIN_URL_TAKEN_MESSAGE } from './profile-identity-draft.validation';

export type EditProfileIdentityError =
  | InfraFailure
  | { kind: 'validation'; fields: ValidationErrors };

export function toEditProfileIdentityError(e: unknown): EditProfileIdentityError {
  const failure = classifyInfraError(e, 'Failed to update the profile.');
  // A 409 is not an infra problem; it is this field being taken. Turning it into
  // a field error is what puts the message under the LinkedIn URL input.
  if (failure.kind === 'api' && failure.status === 409) {
    return { kind: 'validation', fields: { linkedinUrl: LINKEDIN_URL_TAKEN_MESSAGE } };
  }
  return failure;
}
